import { useCallback, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { useProductImport } from '@/hooks/useProductImport';
import { generateImportTemplate, downloadBlob, generateErrorReport } from '@/lib/import';
import { ImportFileUpload } from './ImportFileUpload';
import { ImportPreview } from './ImportPreview';
import { ImportProgress } from './ImportProgress';
import { ImportResults } from './ImportResults';

interface ProductImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories?: Array<{ id: string; name: string }>;
  suppliers?: Array<{ id: string; name: string }>;
}

export function ProductImportDialog({
  open,
  onOpenChange,
  categories = [],
  suppliers = [],
}: ProductImportDialogProps) {
  const {
    step,
    isLoading,
    error,
    validationResult,
    progress,
    results,
    parseFile,
    startImport,
    reset,
    setStep,
  } = useProductImport();

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      // Small delay to allow closing animation
      const timer = setTimeout(reset, 300);
      return () => clearTimeout(timer);
    }
  }, [open, reset]);

  const handleDownloadTemplate = useCallback(() => {
    const blob = generateImportTemplate({
      categories,
      suppliers,
      includeExamples: true,
    });
    downloadBlob(blob, 'blazepos-import-template.xlsx');
  }, [categories, suppliers]);

  const handleFileSelect = useCallback((file: File) => {
    parseFile(file);
  }, [parseFile]);

  const handleStartImport = useCallback(() => {
    startImport();
  }, [startImport]);

  const handleDownloadErrors = useCallback(() => {
    if (!results) return;

    const allErrors = [
      ...results.products.errors.map(e => ({ ...e, sheet: 'Products', field: 'product' })),
      ...results.rawMaterials.errors.map(e => ({ ...e, sheet: 'Raw Materials', field: 'raw_material' })),
      ...results.modifiers.errors.map(e => ({ ...e, sheet: 'Modifiers', field: 'modifier' })),
      ...results.categories.errors.map(e => ({ ...e, sheet: 'Categories', field: 'category' })),
    ];

    const blob = generateErrorReport(allErrors);
    downloadBlob(blob, 'import-errors.xlsx');
  }, [results]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleBack = useCallback(() => {
    if (step === 'preview') {
      setStep('upload');
    }
  }, [step, setStep]);

  // Calculate if we can proceed with import
  const canImport = validationResult && (
    validationResult.summary.products.valid > 0 ||
    validationResult.summary.rawMaterials.valid > 0 ||
    validationResult.summary.modifiers.valid > 0 ||
    validationResult.summary.categories.valid > 0
  );

  const getStepTitle = () => {
    switch (step) {
      case 'upload':
        return 'Import Products';
      case 'preview':
        return 'Review Import Data';
      case 'importing':
        return 'Importing...';
      case 'results':
        return 'Import Complete';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'upload':
        return 'Upload a CSV or Excel file to import products, categories, raw materials, and modifiers.';
      case 'preview':
        return 'Review the data below before importing. Errors are highlighted in red.';
      case 'importing':
        return 'Please wait while your data is being imported.';
      case 'results':
        return 'Your import has been processed. Review the results below.';
    }
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{getStepTitle()}</ResponsiveModalTitle>
          <ResponsiveModalDescription>{getStepDescription()}</ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="flex-1 overflow-y-auto">
          {step === 'upload' && (
            <ImportFileUpload
              onFileSelect={handleFileSelect}
              onDownloadTemplate={handleDownloadTemplate}
              isLoading={isLoading}
              error={error}
            />
          )}

          {step === 'preview' && validationResult && (
            <ImportPreview validationResult={validationResult} />
          )}

          {step === 'importing' && progress && (
            <ImportProgress progress={progress} />
          )}

          {step === 'results' && results && (
            <ImportResults
              results={results}
              onDownloadErrors={handleDownloadErrors}
              onClose={handleClose}
            />
          )}
        </ResponsiveModalBody>

        {/* Footer with navigation buttons */}
        {step !== 'results' && step !== 'importing' && (
          <ResponsiveModalFooter className="flex justify-between border-t pt-4">
            <div>
              {step === 'preview' && (
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              {step === 'preview' && (
                <Button
                  onClick={handleStartImport}
                  disabled={!canImport || isLoading}
                >
                  {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Import {canImport && (
                    <>
                      ({validationResult?.summary.products.valid || 0} products
                      {validationResult?.summary.rawMaterials.valid ? `, ${validationResult.summary.rawMaterials.valid} materials` : ''}
                      {validationResult?.summary.modifiers.valid ? `, ${validationResult.summary.modifiers.valid} modifiers` : ''})
                    </>
                  )}
                </Button>
              )}
            </div>
          </ResponsiveModalFooter>
        )}
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
